

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTripSubOptionModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        option_id: '',
        title: '',
        description: '',
        price: '',
        currency: 'GBP',
        allocation: '',
        sold: 0,
        sequence: '',
        status: 'Active'
    });

    const [formErrors, setFormErrors] = useState({});
    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchOptions();
        }
    }, [isOpen]);

    const fetchOptions = async () => {
        setLoadingOptions(true);
        try {
            const response = await axiosInstance.get('/trip-options/');
            setOptions(response.data);
        } catch (error) {
            console.error('Error fetching trip options:', error);
        } finally {
            setLoadingOptions(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.option_id) errors.option_id = 'Trip Option is required';
        if (!formData.title) errors.title = 'Title is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                option_id: parseInt(formData.option_id),
                price: formData.price ? parseFloat(formData.price) : null,
                allocation: formData.allocation ? parseInt(formData.allocation) : null,
                sold: formData.sold ? parseInt(formData.sold) : 0,
                sequence: formData.sequence ? parseInt(formData.sequence) : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '', option_id: '', title: '', description: '',
                price: '', currency: 'GBP', allocation: '', sold: 0,
                sequence: '', status: 'Active'
            });
        } catch (error) {
            console.error('Error saving Trip Sub-Option:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Trip Sub-Option',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Trip Sub-Option</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Sub-Option Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.option_id} required>
                                        <InputLabel>Trip Option</InputLabel>
                                        <Select
                                            name="option_id"
                                            value={formData.option_id}
                                            onChange={handleInputChange}
                                            label="Trip Option"

                                        >
                                            <MenuItem value=""><em>Select a Trip Option</em></MenuItem>
                                            {options.map(o => (
                                                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.option_id && <MDTypography variant="caption" color="error">{formErrors.option_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        error={!!formErrors.title}
                                        helperText={formErrors.title}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Additional Price"
                                        name="price"
                                        type="number"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            label="Currency"

                                        >
                                            <MenuItem value="GBP">GBP (£)</MenuItem>
                                            <MenuItem value="USD">USD ($)</MenuItem>
                                            <MenuItem value="AUD">AUD (A$)</MenuItem>
                                            <MenuItem value="EUR">EUR (€)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Sequence"
                                        name="sequence"
                                        type="number"
                                        value={formData.sequence}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Allocation"
                                        name="allocation"
                                        type="number"
                                        value={formData.allocation}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Sold"
                                        name="sold"
                                        type="number"
                                        value={formData.sold}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available"
                                        value={(formData.allocation || 0) - (formData.sold || 0)}
                                        disabled
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Sub-Option'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTripSubOptionModal;
